import React, { useEffect, useRef } from "react";
import { TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import CommonButtons from "../../../../../shared/components/CommonButtons";
import { ModalWrapper } from "../../../../../shared/components/ModalWrapper";
import CustomerTable from "./CustomerTable";
import { CustomerData } from "src/shared/services/interfacev2";
import { useCustomerActions } from "./hooks/useCustomerActions";

interface CustomerDialogProps {
  open: boolean;
  handleClose: () => void;
  customers: CustomerData[];
  filteredCustomers: CustomerData[];
  setFilteredCustomers: (customers: CustomerData[]) => void;
  sendorder: (id: string) => void;
  pagenumber: number;
  setPagenumber: (value: number) => void;
  total: { totalRecords: string; totalPage: string };
  setCustomerAdd: (value: boolean) => void;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
}

const SelectCustomer: React.FC<CustomerDialogProps> = ({
  open,
  handleClose,
  customers,
  filteredCustomers,
  setFilteredCustomers,
  sendorder,
  pagenumber,
  setPagenumber,
  total,
  setCustomerAdd,
  searchTerm,
  setSearchTerm,
}) => {
  const hasOpenedRef = useRef(false);

  /** 🔹 Customer actions (dummy setters are fine here) */
  const { createUnknownCustomer } = useCustomerActions(
    () => {},
    () => {},
    () => {},
    () => {},
  );

  // Reset state only when modal first opens
  useEffect(() => {
    if (open && !hasOpenedRef.current) {
      setPagenumber(1);
      setSearchTerm(""); // Clear search when opening
      hasOpenedRef.current = true;
    }

    if (!open) {
      hasOpenedRef.current = false;
    }
  }, [open, setPagenumber, setSearchTerm]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setPagenumber(1); // Reset to first page when searching
  };

  const handleCloseModal = () => {
    handleClose();
  };

  const handleAddCustomer = () => {
    handleClose();
    setCustomerAdd(true);
  };

  const handleUnknownCustomer = async () => {
    const customerId = await createUnknownCustomer();

    if (customerId) {
      sendorder(customerId);
      handleClose();
    }
  };

  const handleSelectCustomer = (id: string) => {
    sendorder(id);
    handleClose();
  };

  const handleNextPage = () => {
    if (Number(total.totalPage) !== pagenumber) {
      setPagenumber(pagenumber + 1);
    }
  };

  const handlePrevPage = () => {
    if (pagenumber > 1) {
      setPagenumber(pagenumber - 1);
    }
  };

  if (!open) return null;

  return (
    <ModalWrapper title='Select Customer' onClose={handleCloseModal} width='max-w-xl'>
      <div className='flex flex-col gap-1 h-full'>
        <div className='flex items-center justify-between gap-3'>
          <TextField
            variant='outlined'
            size='small'
            placeholder='Search customer by name or phone...'
            value={searchTerm}
            onChange={handleSearchChange}
            className='flex-1 bg-white rounded-full'
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon className='text-gray-500' />
                </InputAdornment>
              ),
              style: { borderRadius: "24px", backgroundColor: "#fff" },
            }}
          />
          <CommonButtons label='Add Customer' variant='primary' onClick={handleAddCustomer} />
          <CommonButtons label='Skip Customer' variant='primary' onClick={handleUnknownCustomer} />
        </div>

        <div className='flex-1 overflow-y-auto w-full mt-2'>
          <CustomerTable customers={filteredCustomers} onSelectCustomer={handleSelectCustomer} />
        </div>

        <div className='flex justify-between items-center py-1'>
          <CommonButtons
            label='Previous'
            icon={ArrowLeftIcon}
            className='disabled:bg-gray-300 h-10'
            disabled={pagenumber === 1}
            onClick={handlePrevPage}
          />
          <div className='text-center text-sm'>
            <p>
              Page: {pagenumber}/{total.totalPage}
            </p>
            <p>Total Records: {total.totalRecords}</p>
          </div>
          <CommonButtons
            label='Next'
            icon={ArrowRightIcon}
            className='disabled:bg-gray-300 h-10'
            disabled={Number(total.totalPage) === pagenumber}
            onClick={handleNextPage}
          />
        </div>
      </div>
    </ModalWrapper>
  );
};

export default SelectCustomer;
